import {
	BrokerCommunication,
	type CommunicationFilterValue,
	isContractStatus,
	PAGES,
} from "@dune/types";
import { Input, PaginatedDataTable, ResizableLayout } from "@dune/ui";
import { SearchSm } from "@untitled-ui/icons-react";
import { useQueryState } from "nuqs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllCommunications } from "../../../api/queries/communications.queries";
import PanelProvider from "../../../component/layout/PanelProvider";
import { useDebounce } from "../../../hooks/useDebounce";
import { usePanel } from "../../../hooks/usePanel";
import {
	useEmailActions,
	useIsComposerDirty,
	useIsEmailComposerOpen,
	useSelectedEmailId,
} from "../../../stores/email.store";
import { useCurrentPanel } from "../../../stores/panel.store";
import { usePaginationStore } from "../../../stores/pagination.store";
import { communicationColumns } from "./CommunicationColumns";
import { CommunicationFilters } from "./CommunicationFilters";
import { parseAsInteger } from "nuqs";

const CommunicationListPage = () => {
	const navigate = useNavigate();
	const storedLimit = usePaginationStore((state) => state.limit);
	const setStoredLimit = usePaginationStore((state) => state.setLimit);

	const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));
	const [limitParam, setLimitParam] = useQueryState(
		"limit",
		parseAsInteger.withDefault(storedLimit),
	);
	const [filtersParam, setFiltersParam] = useQueryState("filters", {
		defaultValue: "unread",
	});
	const [searchInput, setSearchInput] = useState("");
	const debouncedSearch = useDebounce(searchInput, 500);

	const limit = limitParam ?? storedLimit;

	const activeFilters = useMemo(() => {
		if (!filtersParam) return new Set<CommunicationFilterValue>();
		return new Set(
			filtersParam.split(",").filter(Boolean) as CommunicationFilterValue[],
		);
	}, [filtersParam]);

	const handleToggleFilter = useCallback(
		(filter: CommunicationFilterValue) => {
			const newFilters = new Set(activeFilters);
			if (newFilters.has(filter)) {
				newFilters.delete(filter);
			} else {
				newFilters.add(filter);
			}
			const filtersString = [...newFilters].join(",");
			setFiltersParam(filtersString);
			setPage(1);
		},
		[activeFilters, setFiltersParam, setPage],
	);

	// Reset la page quand la recherche change
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset page on search change
	useEffect(() => {
		setPage(1);
	}, [debouncedSearch, setPage]);

	const panel = usePanel();
	const currentPanel = useCurrentPanel();
	const selectedEmailId = useSelectedEmailId();
	const { selectEmail, clearEmailSelection } = useEmailActions();
	const isComposerOpen = useIsEmailComposerOpen();
	const isComposerDirty = useIsComposerDirty();

	const {
		data: communicationsData,
		isLoading,
		error,
	} = useGetAllCommunications({
		page,
		limit,
		filters: filtersParam || undefined,
		search: debouncedSearch || undefined,
	});

	useEffect(() => {
		if (
			selectedEmailId &&
			communicationsData?.data &&
			communicationsData.data.length > 0
		) {
			const selectedMail = communicationsData.data.find(
				(mail) => mail.id.toString() === selectedEmailId,
			);
			if (selectedMail) {
				panel.showEmailPreview({
					mail: selectedMail,
					brokerId: 0,
					onClose: () => {
						clearEmailSelection();
					},
				});
			}
		}
	}, [selectedEmailId, communicationsData, panel, clearEmailSelection]);

	const handleRowClick = (mail: BrokerCommunication) => {
		if (isComposerOpen && isComposerDirty) {
			return;
		}

		if (!mail.relatedEntities || mail.relatedEntities.length === 0) {
			selectEmail(mail.id.toString());
			return;
		}

		const entity = mail.relatedEntities[0];

		if (
			entity.entityType === "PROPOSITION" &&
			entity.entityReference === entity.entityId
		) {
			selectEmail(mail.id.toString());
			return;
		}

		let redirectPath = "";

		switch (entity.entityType) {
			case "BROKER":
				redirectPath = `/brokers/${entity.entityId}?tab=communications`;
				break;
			case "CONSTRUCTION_SITE":
				redirectPath = `/projects/${entity.entityId}?tab=communications`;
				break;
			case "PROPOSITION":
				if (entity.status && isContractStatus(entity.status)) {
					redirectPath = `${PAGES.CONTRACTS.show.withParams(
						entity.entityId.toString(),
					)}?tab=communications`;
				} else {
					redirectPath = `${PAGES.PROPOSALS.show.withParams(
						entity.entityId.toString(),
					)}?tab=communications`;
				}
				break;
			case "GFA":
				redirectPath = `/gfa/${entity.entityId}?tab=communications`;
				break;
			default:
				selectEmail(mail.id.toString());
				return;
		}

		navigate(redirectPath);
	};

	if (error) {
		return (
			<ResizableLayout>
				<ResizableLayout.Main>
					<div className="p-6">
						<div className="text-red-600">
							Erreur lors du chargement des communications: {error.message}
						</div>
					</div>
				</ResizableLayout.Main>
				{currentPanel && (
					<ResizableLayout.Panel>
						<PanelProvider />
					</ResizableLayout.Panel>
				)}
			</ResizableLayout>
		);
	}

	return (
		<ResizableLayout>
			<ResizableLayout.Main>
				<div className="h-full">
					<div className="flex items-center justify-between mb-6">
						<h1 className="text-2xl font-semibold text-gray-900">
							Communications
						</h1>
					</div>

					<div className="flex items-center gap-4 mb-4">
						<CommunicationFilters
							activeFilters={activeFilters}
							onToggle={handleToggleFilter}
						/>
						<div className="ml-auto w-80">
							<Input
								type="text"
								placeholder="Rechercher par sujet, expéditeur, destinataire..."
								value={searchInput}
								onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
									setSearchInput(event.target.value)
								}
								iconOrText={<SearchSm />}
								iconOrTextPosition="right"
							/>
						</div>
					</div>

					<PaginatedDataTable
						data={communicationsData?.data || []}
						columns={communicationColumns}
						meta={{
							page: communicationsData?.meta?.page || 1,
							limit: communicationsData?.meta?.limit || limit,
							total: communicationsData?.meta?.total || 0,
							totalPages: communicationsData?.meta?.totalPages || 0,
							hasNextPage: communicationsData?.meta?.hasNextPage || false,
							hasPreviousPage:
								communicationsData?.meta?.hasPreviousPage || false,
						}}
						onPageChange={(newPage) => setPage(newPage)}
						onLimitChange={(newLimit) => {
							setStoredLimit(newLimit);
							setLimitParam(newLimit);
							setPage(1);
						}}
						onRowClick={handleRowClick}
						isLoading={isLoading}
						noResultsText="Aucune communication"
						enableSorting={true}
						fixedLayout={true}
					/>
				</div>
			</ResizableLayout.Main>
			{currentPanel && (
				<ResizableLayout.Panel>
					<PanelProvider />
				</ResizableLayout.Panel>
			)}
		</ResizableLayout>
	);
};

export default CommunicationListPage;
